import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { formatPatientDob } from '@/lib/dateUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Check, ArrowLeft, AlertTriangle, ClipboardCheck, Send, Lock, Loader2,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { validateNarrativeCptAlignment } from '@/lib/validation/narrativeMismatchGuard';
import { useSignOpNote } from '@/hooks/mutations/useSignOpNote';
import { createOfficeManagerFlag, saveOfficeManagerFlag } from '@/lib/surgeon-console/finalizedAuthorization';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoPath } from '@/contexts/DemoPathContext';
import { canonicalDemoCaseId, markDemoPostOpSigned } from '@/lib/demo/demoPresentationState';
import { shouldShowSurgeryRevenueForDemoPath } from '@/lib/demo/demoPathVisibility';
import { isPerfectWorldDemo, normalizeDemoPostOpResponse } from '@/lib/demo/perfectWorldDemo';

import { OpNoteResponse } from './opNoteResponseTypes';
import { CleanClaimScorecard } from './review/CleanClaimScorecard';
import { RevenueImpactCard } from './review/RevenueImpactCard';
import { ComplianceGate, ComplianceGap } from './review/ComplianceGate';
import { EvidencePanel } from './review/EvidencePanel';
import { NoteActionBar } from './review/NoteActionBar';
import { ReconciledNoteCard } from './review/ReconciledNoteCard';
import { ResolutionFieldConfig } from './review/InlineResolutionField';
import { LinkTransitionOverlay } from './review/LinkTransitionOverlay';
import { GuidedEditFAB } from './review/GuidedEditFAB';
import { playSentHandoffChime, SentHandoffOverlay } from './review/SentHandoffOverlay';

interface PostOpReviewDashboardProps {
  response: OpNoteResponse;
  /** Authorization/case ID for the centralized sign mutation. */
  caseId?: string | null;
  patientName: string;
  patientDob?: string;
  patientMrn?: string;
  patientSex?: string | null;
  payerName?: string | null;
  facilityLocation?: string | null;
  caseEncounterNo?: string | null;
  onBack: () => void;
  onFinalize: () => void;
  onDeferToOfficeManager?: () => void;
  initialEbl?: string;
  initialOpTime?: string;
  initialHighComplexity?: boolean;
  initialLaterality?: string | null;
  initialPositioning?: string;
  initialAnesthesia?: string;
  initialImplantsVerified?: boolean;
  initialConservativeTherapy?: boolean;
  initialAssistant?: string;
  onDemoResetCase?: (caseId: string) => void;
}

const RESOLUTION_FIELDS: ResolutionFieldConfig[] = [
  {
    id: 'patient_age',
    label: 'Verify Patient Age',
    placeholder: '',
    options: [],
    defaultValue: '',
    attestation: false,
    assistText: 'Patient age is calculated from DOB. Verify for accuracy.',
  },
  {
    id: 'laterality',
    label: 'Verify Laterality',
    placeholder: '',
    attestation: true,
    attestationLabel: 'Verify: Laterality',
    attestationConfirmedLabel: 'Laterality Verified',
    options: ['RIGHT — Verified', 'LEFT — Verified', 'Bilateral — Verified'],
    assistText: 'CMS requires explicit laterality confirmation matching the Pre-Auth. Modifier -RT/-LT is applied based on this attestation. Mismatch triggers automatic denial.',
  },
  {
    id: 'anesthesia_type',
    label: 'Verify Anesthesia',
    placeholder: '',
    attestation: true,
    attestationLabel: 'Verify: Anesthesia Type',
    attestationConfirmedLabel: 'General',
    options: ['General', 'Regional', 'MAC', 'General + Regional', 'Spinal'],
    assistText: 'Anesthesia type must match the anesthesia record. Discrepancies between operative note and anesthesia billing trigger audit flags.',
  },
  {
    id: 'patient_position',
    label: 'Verify Position',
    placeholder: '',
    attestation: true,
    attestationLabel: 'Verify: Patient Position',
    attestationConfirmedLabel: 'Position Verified',
    options: ['Beach Chair', 'Lateral Decubitus', 'Supine', 'Prone', 'Lithotomy'],
    assistText: 'Patient positioning must be documented for medical necessity of the surgical approach and to support CPT code selection.',
  },
  {
    id: 'conservative_therapy',
    label: 'Attest Conservative Therapy',
    placeholder: '',
    attestation: true,
    attestationLabel: 'Attest: Patient failed conservative therapy',
    attestationConfirmedLabel: 'exhausted conservative therapy including physical therapy and medications',
    options: [
      'exhausted >12 weeks of conservative therapy including physical therapy and NSAIDs',
      'exhausted >6 weeks of conservative therapy including PT and corticosteroid injections',
      'exhausted >6 months of conservative therapy including PT, injections, and bracing',
    ],
    assistText: 'Payer policies require explicit attestation of conservative therapy duration to validate medical necessity. Without this, the claim may be denied.',
  },
  {
    id: 'implant_verification',
    label: 'Verify Implants',
    placeholder: '',
    options: ['Implants verified per operative record'],
    defaultValue: '',
    attestation: false,
    assistText: 'Implant documentation must match the facility charge sheet and manufacturer invoice. Discrepancies are a top audit trigger.',
  },
  {
    id: 'ebl_verification',
    label: 'Verify EBL',
    placeholder: '',
    attestation: true,
    attestationLabel: 'Verify: EBL',
    attestationConfirmedLabel: 'Verified',
    options: ['5 mL', '10 mL', '15 mL', '< 5 mL (minimal)', '25 mL', '50 mL'],
    assistText: 'Estimated blood loss must be documented.',
  },
  {
    id: 'time_verification',
    label: 'Verify Operative Time',
    placeholder: '',
    attestation: true,
    attestationLabel: 'Verify: Operative Time',
    attestationConfirmedLabel: 'Verified',
    options: ['30 mins', '45 mins', '60 mins', '75 mins', '90 mins', '120 mins'],
    assistText: 'Total surgical time must match the anesthesia record within a reasonable margin.',
  },
  {
    id: 'assistant_verification',
    label: 'Verify Assistant',
    placeholder: '',
    attestation: true,
    attestationLabel: 'Verify: Assistant Utilized',
    attestationConfirmedLabel: 'The surgical assistant was utilized and essential to the procedure for positioning the extremity, retraction, and assisting with instrumentation so the primary surgeon had two hands free for dissection and implant placement.',
    options: ['The surgical assistant was utilized and essential to the procedure for positioning the extremity, retraction, and assisting with instrumentation so the primary surgeon had two hands free for dissection and implant placement.', 'None utilized'],
    assistText: 'If an assistant surgeon was used, Modifier -80 is required. Incorrect assistant documentation is a common cause of claim denials.',
  },
];

export function PostOpReviewDashboard({
  response,
  caseId,
  patientName,
  patientDob,
  patientMrn,
  patientSex,
  payerName,
  facilityLocation,
  caseEncounterNo,
  onBack,
  onFinalize,
  onDeferToOfficeManager,
  initialEbl,
  initialOpTime,
  initialHighComplexity = false,
  initialLaterality,
  initialPositioning,
  initialAnesthesia,
  initialImplantsVerified,
  initialConservativeTherapy,
  initialAssistant,
  onDemoResetCase,
}: PostOpReviewDashboardProps) {
  const [isSigned, setIsSigned] = useState(false);
  const [showOpNoteSentOverlay, setShowOpNoteSentOverlay] = useState(false);
  const signMutation = useSignOpNote();
  const { isDemoMode } = useDemoMode();
  const { demoPath } = useDemoPath();
  const { profile } = useAuthContext();
  const showRevenueImpact = shouldShowSurgeryRevenueForDemoPath({ demoPath });
  const reviewCaseId = caseId || caseEncounterNo || patientName;
  const perfectWorldDemo = isPerfectWorldDemo(isDemoMode, reviewCaseId);
  const effectiveResponse = useMemo(() => (
    perfectWorldDemo ? normalizeDemoPostOpResponse(response, reviewCaseId) : response
  ), [perfectWorldDemo, response, reviewCaseId]);
  const surgeonName = profile?.full_name
    ? (profile.full_name.startsWith('Dr.') ? profile.full_name : `Dr. ${profile.full_name}`)
    : 'Surgeon';
  const payerDestination = payerName?.trim() || 'the insurance payer';
  const opNoteSentDescription = `${patientName}'s operative note was sent to ${payerDestination} and Epic / EHR.`;

  // Calculate age from DOB using local date parsing
  const calculatedAge = useMemo(() => {
    if (!patientDob) return null;
    const parts = patientDob.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (parts) {
      const [, m, d, y] = parts;
      const birth = new Date(Number(y), Number(m) - 1, Number(d));
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    }
    const isoParts = patientDob.match(/(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
    if (isoParts) {
      const [, y, m, d] = isoParts;
      const birth = new Date(Number(y), Number(m) - 1, Number(d));
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    }
    return null;
  }, [patientDob]);

  // Build dynamic resolution fields based on passed-in values
  const dynamicFields = useMemo(() => {
    const requiresConservativeAttestation = initialConservativeTherapy !== false;
    const requiresImplantVerification =
      initialImplantsVerified === true || (effectiveResponse.implants_and_biologics ?? []).length > 0;
    const requiresAssistantVerification = Boolean(initialAssistant);

    return RESOLUTION_FIELDS.filter((field) => {
      if (field.id === 'conservative_therapy') return requiresConservativeAttestation;
      if (field.id === 'implant_verification') return requiresImplantVerification;
      if (field.id === 'assistant_verification') return requiresAssistantVerification;
      return true;
    }).map(f => {
      if (f.id === 'patient_age' && calculatedAge !== null) {
        return {
          ...f,
          defaultValue: `${calculatedAge}`,
          options: [`${calculatedAge}`],
        };
      }
      if (f.id === 'ebl_verification' && initialEbl && initialEbl !== '0') {
        const eblVal = `${initialEbl} mL`;
        return {
          ...f,
          attestationLabel: `Verify: ${eblVal}`,
          attestationConfirmedLabel: eblVal,
          options: [eblVal, ...f.options!.filter(o => o !== eblVal)],
        };
      }
      if (f.id === 'time_verification' && initialOpTime && initialOpTime !== '0') {
        const timeVal = `${initialOpTime} mins`;
        return {
          ...f,
          attestationLabel: `Verify: ${timeVal}`,
          attestationConfirmedLabel: timeVal,
          options: [timeVal, ...f.options!.filter(o => o !== timeVal)],
        };
      }
      return f;
    });
  }, [calculatedAge, effectiveResponse.implants_and_biologics, initialAssistant, initialConservativeTherapy, initialEbl, initialImplantsVerified, initialOpTime]);

  // Conditionally add BMI high-complexity field
  const allFields = useMemo(() => {
    if (!initialHighComplexity) return dynamicFields;
    const bmiField: ResolutionFieldConfig = {
      id: 'high_complexity_bmi',
      label: 'Verify High Complexity (BMI >40)',
      placeholder: '',
      attestation: true,
      attestationLabel: 'Verify: High Complexity (BMI >40)',
      attestationConfirmedLabel: 'This procedure involved increased complexity due to morbid obesity (BMI > 40). Extensive adipose tissue required prolonged, tedious dissection, specialized retraction, and increased operative time by approximately 30 minutes beyond a standard case.',
      options: [
        'This procedure involved increased complexity due to morbid obesity (BMI > 40). Extensive adipose tissue required prolonged, tedious dissection, specialized retraction, and increased operative time by approximately 30 minutes beyond a standard case.',
        'Increased complexity due to patient body habitus (BMI > 40) requiring additional operative time.',
      ],
      assistText: 'Modifier -22 requires documentation of substantially greater work. BMI > 40 qualifies when operative time exceeds standard by >25% and additional technical difficulty is documented.',
    };
    return [...dynamicFields, bmiField];
  }, [dynamicFields, initialHighComplexity]);

  const [resolvedValues, setResolvedValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    // Auto-resolve from Smart Builder data
    if (initialEbl && initialEbl !== '0') {
      initial['ebl_verification'] = `${initialEbl} mL`;
    }
    if (initialOpTime && initialOpTime !== '0') {
      initial['time_verification'] = `${initialOpTime} mins`;
    }
    if (calculatedAge !== null) {
      initial['patient_age'] = `${calculatedAge}`;
    }
    if (initialLaterality) {
      const lat = initialLaterality === 'right' ? 'RIGHT' : initialLaterality === 'left' ? 'LEFT' : 'Bilateral';
      initial['laterality'] = `${lat} — Verified`;
    }
    if (initialAnesthesia) {
      initial['anesthesia_type'] = initialAnesthesia;
    }
    if (initialPositioning) {
      initial['patient_position'] = initialPositioning;
    }
    if (initialConservativeTherapy) {
      initial['conservative_therapy'] = 'exhausted >12 weeks of conservative therapy including physical therapy and NSAIDs';
    }
    if (initialImplantsVerified) {
      initial['implant_verification'] = 'Implants verified per operative record';
    }
    if (initialAssistant) {
      initial['assistant_verification'] = initialAssistant;
    }
    return initial;
  });
  const [mode, setMode] = useState<'audit' | 'transition' | 'sign'>('audit');

  const warnings = effectiveResponse.warnings ?? [];
  const effectiveWarnings = perfectWorldDemo ? [] : warnings;
  const primaryCpt = effectiveResponse.primary_cpt ?? { code: '', description: '', modifiers: [] };
  const additionalCpts = effectiveResponse.additional_cpts ?? [];

  // CPT-narrative mismatch validation gate
  const mismatchResult = useMemo(() => {
    const allCptCodes = [primaryCpt.code, ...additionalCpts.map(c => c.code)].filter(Boolean);
    return validateNarrativeCptAlignment(allCptCodes, effectiveResponse.optimized_note);
  }, [primaryCpt, additionalCpts, effectiveResponse.optimized_note]);

  useEffect(() => {
    if (!perfectWorldDemo && !mismatchResult.valid) {
      toast.error('⚠️ Narrative-CPT Mismatch Detected', {
        description: mismatchResult.mismatches[0],
        duration: 10000,
      });
    }
  }, [mismatchResult, perfectWorldDemo]);

  const baseFinancialImpact = effectiveResponse.financial_impact ?? {
    total_estimated_payout: 0,
    facility_revenue: 0,
    professional_revenue: 0,
    summary_text: '',
  };

  const complianceGaps: ComplianceGap[] = allFields.map(f => ({
    id: f.id,
    label: f.label.startsWith('Verify') || f.label.startsWith('Attest')
      ? `${f.label} Required`
      : f.label,
    resolved: (resolvedValues[f.id] ?? '').trim().length > 0,
  }));

  const effectiveComplianceGaps = perfectWorldDemo
    ? complianceGaps.map(gap => ({ ...gap, resolved: true }))
    : complianceGaps;
  const unresolvedCount = effectiveComplianceGaps.filter(g => !g.resolved).length;
  const canAudit = unresolvedCount === 0;
  const isAuditMode = mode === 'audit';
  const isSignMode = mode === 'sign';
  const [isEditing, setIsEditing] = useState(false);

  const financialImpact = useMemo(() => {
    if (unresolvedCount === 0 || mode === 'sign') {
      return {
        ...baseFinancialImpact,
        facility_revenue: baseFinancialImpact.facility_revenue || 11500,
        professional_revenue: baseFinancialImpact.professional_revenue || 1150,
        total_estimated_payout: baseFinancialImpact.total_estimated_payout || 12650,
        summary_text: baseFinancialImpact.summary_text ||
          `CPT ${primaryCpt.code}${primaryCpt.modifiers?.length ? `-${primaryCpt.modifiers.join('-')}` : ''} estimated payout based on payer fee schedule assumptions.`,
      };
    }
    return baseFinancialImpact;
  }, [baseFinancialImpact, mode, primaryCpt.code, primaryCpt.modifiers, unresolvedCount]);

  // DOM-based scroll: query all data-compliance-gap elements in document order
  const scrollToNextUnresolved = useCallback(() => {
    const allGapEls = Array.from(document.querySelectorAll<HTMLElement>('[data-compliance-gap]'));
    for (const el of allGapEls) {
      const gapId = el.getAttribute('data-compliance-gap');
      if (gapId && !(resolvedValues[gapId] ?? '').trim()) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2', 'rounded-md');
        setTimeout(() => el.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2', 'rounded-md'), 3000);
        return;
      }
    }
  }, [resolvedValues]);

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
    setTimeout(() => scrollToNextUnresolved(), 150);
  }, [scrollToNextUnresolved]);

  const handleNextRequired = useCallback(() => {
    scrollToNextUnresolved();
  }, [scrollToNextUnresolved]);

  const handleFlagForOfficeManager = useCallback(() => {
    const unresolved = effectiveComplianceGaps.filter(gap => !gap.resolved);
    const affectedCodes = [primaryCpt.code, ...additionalCpts.map(cpt => cpt.code)].filter(Boolean);
    saveOfficeManagerFlag(createOfficeManagerFlag({
      caseId: caseId || caseEncounterNo || `post-op-${patientName}`,
      patientName,
      title: 'Post-op signature blocker',
      detail: unresolved.length
        ? unresolved.map(gap => gap.label).join('; ')
        : 'Post-op note needs office-manager reconciliation before final signature.',
      affectedCodes,
      source: 'post-op',
      severity: 'staff_hold',
    }));
    toast.info('Flagged for office manager', {
      description: 'The case is on staff hold so the surgeon can keep moving.',
    });
    onDeferToOfficeManager?.();
  }, [additionalCpts, caseEncounterNo, caseId, effectiveComplianceGaps, onDeferToOfficeManager, patientName, primaryCpt.code]);

  const handleResolve = useCallback((id: string, value: string) => {
    setResolvedValues(prev => {
      const next = { ...prev, [id]: value };
      if (!value.trim()) return next;

      // Auto-scroll to next unresolved using DOM order after 400ms
      setTimeout(() => {
        const allGapEls = Array.from(document.querySelectorAll<HTMLElement>('[data-compliance-gap]'));
        let foundUnresolved = false;
        for (const el of allGapEls) {
          const gapId = el.getAttribute('data-compliance-gap');
          if (gapId && !(next[gapId] ?? '').trim()) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2', 'rounded-md');
            setTimeout(() => el.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2', 'rounded-md'), 3000);
            foundUnresolved = true;
            break;
          }
        }
        if (!foundUnresolved) {
          // All gaps resolved — scroll to Sign & Route button
          const signBtn = document.querySelector('[data-sign-button]');
          if (signBtn) {
            signBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 400);

      return next;
    });
  }, []);

  const handleAudit = () => {
    if (!canAudit) {
      toast.error('Resolve all compliance items before auditing.');
      return;
    }
    setIsEditing(false);
    setMode('transition');
  };

  // Auto-trigger audit when all compliance items are resolved (removes manual Audit button)
  useEffect(() => {
    if (isAuditMode && canAudit) {
      handleAudit();
    }
  }, [canAudit, isAuditMode]);

  const handleTransitionComplete = useCallback(() => {
    setMode('sign');
    toast.success('Compliance chain verified — ready to sign.');
  }, []);

  // PESSIMISTIC: do not flip UI to "signed" until the centralized mutation
  // resolves successfully (DB write + audit log entry confirmed).
  const handleFinalize = async () => {
    if (signMutation.isPending || isSigned) return;
    try {
      const result = await signMutation.mutateAsync({ caseId: caseId ?? '' });
      if (result.demo) {
        markDemoPostOpSigned(result.caseId);
      }
      setIsSigned(true);
      onFinalize();
      toast.success('Operative note finalized & signed', {
        description: 'Ready for EHR sync or export.',
      });
    } catch (err: any) {
      toast.error('Sign failed', { description: err?.message ?? 'Please retry.' });
    }
  };

  const handleSendOpNote = useCallback(() => {
    playSentHandoffChime();
    setShowOpNoteSentOverlay(true);
    toast.success('Operative note sent', {
      description: opNoteSentDescription,
    });
    window.setTimeout(() => {
      setShowOpNoteSentOverlay(false);
    }, 2100);
  }, [opNoteSentDescription]);

  return (
    <div className="space-y-5">
      <SentHandoffOverlay
        show={showOpNoteSentOverlay}
        title="Operative note sent."
        description={opNoteSentDescription}
      />
      <LinkTransitionOverlay show={mode === 'transition'} onComplete={handleTransitionComplete} />

      {/* Back */}
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2 print:hidden">
        <ArrowLeft className="w-4 h-4" /> Back to Smart Builder
      </Button>

      {/* Signed Banner */}
      {isSigned && (
        <div className="flex flex-col gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 print:hidden sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">
              Operative note finalized & signed by {surgeonName} — Ready for EHR sync or export.
            </span>
          </div>
          {isDemoMode && onDemoResetCase && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
              onClick={() => {
                const resetCaseId = canonicalDemoCaseId(caseId) || caseId || '';
                if (resetCaseId) onDemoResetCase(resetCaseId);
              }}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset Demo Case
            </Button>
          )}
        </div>
      )}

      {/* Layout */}
      <div className={isSignMode ? 'grid grid-cols-1 lg:grid-cols-3 gap-4 transition-all duration-500 ease-in-out' : 'transition-all duration-500 ease-in-out'}>
        {/* Main Column */}
        <div className={isSignMode ? 'lg:col-span-2 space-y-4' : 'space-y-4'}>
          {/* Compliance Gate */}
          <div className={`${isAuditMode ? 'sticky top-0 z-30 max-h-[50vh] overflow-y-auto' : ''} print:hidden`}>
            <ComplianceGate
              gaps={effectiveComplianceGaps}
              onStartEdit={handleStartEdit}
              onFlagForOfficeManager={unresolvedCount > 0 ? handleFlagForOfficeManager : undefined}
              isEditing={isEditing}
            />
          </div>

          {/* Warnings */}
          {effectiveWarnings.length > 0 && (
            <div className="flex items-start gap-3 p-4 rounded-lg border-2 border-amber-400 bg-amber-50 print:hidden">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-amber-800">Reconciliation Warnings</h4>
                <ul className="mt-1 space-y-1">
                  {effectiveWarnings.map((w, i) => (
                    <li key={i} className="text-sm text-amber-700">• {w}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Reconciled Operative Note */}
          <ReconciledNoteCard
            isSigned={isSigned}
            resolutionFields={allFields}
            resolvedValues={resolvedValues}
            onResolve={handleResolve}
            engineVersion={effectiveResponse.reconciliation_engine_version}
            patientName={patientName}
            patientDob={formatPatientDob(patientDob, undefined)}
            patientMrn={patientMrn}
            patientSex={patientSex}
            surgeonName={surgeonName}
            facilityLocation={facilityLocation || 'Facility'}
            caseEncounterNo={caseEncounterNo || ''}
            highComplexityText={initialHighComplexity ? 'Please note: This procedure involved increased complexity due to morbid obesity (BMI > 40). Extensive adipose tissue required prolonged, tedious dissection, specialized retraction, and increased operative time by approximately 30 minutes beyond a standard case.' : undefined}
            optimizedNote={effectiveResponse.optimized_note}
            primaryCpt={primaryCpt}
            additionalCpts={additionalCpts}
            icd10Codes={effectiveResponse.icd10_codes}
          />

          {!isAuditMode && (
            <NoteActionBar
              note={effectiveResponse.optimized_note}
              patientName={patientName}
              isSigned={isSigned}
              canSign={!signMutation.isPending}
              isSigning={signMutation.isPending}
              onFinalize={handleFinalize}
              onSendToEhr={handleSendOpNote}
            />
          )}

          {/* Audit-mode fallback CTA — pessimistic, blocks until DB confirms write */}
          {isAuditMode && (
          <div className="print:hidden mt-6 flex flex-col md:flex-row gap-3">
            <Button
              data-sign-button
              onClick={handleFinalize}
              disabled={isSigned || signMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-primary-foreground font-semibold text-lg h-14 px-8 rounded-xl shadow-lg w-full md:w-auto disabled:bg-muted disabled:text-muted-foreground disabled:opacity-50"
              size="lg"
            >
              {isSigned ? (
                <><Lock className="w-5 h-5 mr-2" /> Signed & Locked</>
              ) : signMutation.isPending ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Signing & Recording Audit…</>
              ) : (
                <><Send className="w-5 h-5 mr-2" /> Sign & Route to EHR</>
              )}
            </Button>
            {unresolvedCount > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleFlagForOfficeManager}
                className="h-14 px-6 rounded-xl border-amber-300 text-amber-900 hover:bg-amber-50"
              >
                Flag for Office Manager
              </Button>
            )}
          </div>
          )}
        </div>

        {/* Right Column — Sign mode only */}
        {isSignMode && (
          <div className="lg:col-span-1 space-y-4 animate-fade-in print:hidden">
            <CleanClaimScorecard
              probability={effectiveResponse.clean_claim_probability}
              riskLevel={effectiveResponse.audit_risk_score}
            />
            {showRevenueImpact && (
              <RevenueImpactCard
                primaryCpt={primaryCpt}
                additionalCpts={additionalCpts}
                financialImpact={financialImpact}
              />
            )}
            <EvidencePanel
              evidencePayloads={effectiveResponse.evidence_payloads ?? []}
              implants={effectiveResponse.implants_and_biologics ?? []}
            />
            <Card className="border-border/60">
              <CardContent className="p-4 space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Audit Trail
                </h4>
                <div className="text-[11px] text-muted-foreground space-y-1">
                  <p>Patient: {patientName}</p>
                  <p>Surgeon: {surgeonName}</p>
                  <p>Note generated: {new Date().toLocaleString()}</p>
                  <p>Engine: {effectiveResponse.reconciliation_engine_version}</p>
                  <p>Risk Level: {effectiveResponse.audit_risk_score}</p>
                  <p>Compliance Items: {effectiveComplianceGaps.length} (all resolved)</p>
                  <p>Warnings: {effectiveWarnings.length}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
